<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            access: {
                title: "Gérer l'accès aux bâtiments",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

type BuildingFormatted = {
    _id: string
    name: string
    address?: string
    icon?: string
    visible_to?: Array<{ _id_user: string }>
    access?: boolean
}

type Props = {
    /**
     * Identifiant de l'utilisateur
     */
    userId: string
    /**
     * Prénom de l'utilisateur
     */
    firstname: string
    /**
     * Nom de l'utilisateur
     */
    lastname: string
    /**
     * Nom d'utilisateur (email)
     */
    username: string
    /**
     * Avatar de l'utilisateur
     */
    avatar?: string | null
    /**
     * Liste des bâtiments avec leurs accès
     */
    buildings: BuildingFormatted[]
    /**
     * État de chargement
     */
    loading?: boolean
    /**
     * Indique si l'utilisateur est administrateur
     */
    admin?: boolean
}

// ================================
// PROPS & EMITS
// ================================
withDefaults(defineProps<Props>(), {
    avatar: null,
    buildings: () => [],
    loading: false,
    admin: false,
})

const emit = defineEmits<{
    /**
     * Émis lors de la mise à jour d'un accès
     */
    "update:access": [{ access: boolean; buildingId: string }]
}>()

/**
 * Contrôle la visibilité du dialog
 */
const [visible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

// ================================
// INTERACTIONS
// ================================

/**
 * Gère la mise à jour d'un accès
 * @param payload - Payload contenant l'accès et l'identifiant du bâtiment
 */
function onUpdateAccess(payload: {
    access: boolean
    buildingId: string
}): void {
    emit("update:access", payload)
}
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        dismissable-mask
        class="access-building-by-user-dialog max-w-[50rem] w-full"
        :header="t('access.title')"
    >
        <AccessListBuilding
            :user-id="userId"
            :firstname="firstname"
            :lastname="lastname"
            :username="username"
            :avatar="avatar"
            :buildings="buildings"
            :loading="loading"
            :admin="admin"
            :visible="visible"
            @update:access="onUpdateAccess"
        />
    </BaseDialog>
</template>
